import React, { useState, useRef, useCallback, useEffect } from 'react';

const Photo = ({ image, alt, liveVideo }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef(null);
  const longPressTimer = useRef(null);

  // Reset state when image changes
  useEffect(() => {
    setLoaded(false);
    setError(false);
    setShowVideo(false);
  }, [image]);

  const startLiveVideo = useCallback(() => {
    if (!liveVideo) return;
    setShowVideo(true);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  }, [liveVideo]);

  const stopLiveVideo = useCallback(() => {
    setShowVideo(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, []);

  // Desktop: hover
  const handleMouseEnter = () => startLiveVideo();
  const handleMouseLeave = () => stopLiveVideo();

  // Mobile: long press
  const handleTouchStart = (e) => {
    if (!liveVideo) return;
    longPressTimer.current = setTimeout(() => {
      e.preventDefault();
      startLiveVideo();
    }, 300);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    stopLiveVideo();
  };

  return (
    <div
      className="photo"
      onMouseEnter={liveVideo ? handleMouseEnter : undefined}
      onMouseLeave={liveVideo ? handleMouseLeave : undefined}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      {!loaded && !error && <div className="photo-placeholder">Loading photo...</div>}
      {error && <div className="photo-placeholder photo-error">Could not load photo</div>}

      {liveVideo && (
        <span className="live-badge">LIVE</span>
      )}

      <img
        src={image}
        alt={alt || ''}
        className={`img-fluid${loaded ? '' : ' photo-hidden'}`}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />

      {liveVideo && (
        <video
          ref={videoRef}
          src={liveVideo}
          className={`live-video${showVideo ? ' live-video-active' : ''}`}
          muted
          playsInline
          loop
          preload="none"
        />
      )}
    </div>
  );
};

const VideoEntry = ({ video, poster }) => {
  return (
    <div className="photo video-entry">
      <span className="video-badge">VIDEO</span>
      <video
        src={video}
        poster={poster}
        controls
        playsInline
        preload="metadata"
        className="img-fluid"
      />
    </div>
  );
};

export { VideoEntry };
export default Photo;
